/*
  Offer picker, gallery, sticky bar and delivery estimate.
  No libraries. Talks to Dawn's cart drawer through its public methods.
*/

// Both the buy box and the video section include this file; only run it once.
(() => {
  if (window.mkScriptsLoaded) return;
  window.mkScriptsLoaded = true;

  const mkReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!customElements.get('offer-picker')) {
    customElements.define(
      'offer-picker',
      class OfferPicker extends HTMLElement {
        connectedCallback() {
          this.form = this.querySelector('form');
          this.idInput = this.form.querySelector('input[name="id"]');
          this.qtyInput = this.form.querySelector('input[name="quantity"]');
          this.button = this.querySelector('[data-atc]');
          this.errorEl = this.querySelector('[data-error]');
          this.express = this.querySelector('[data-express]');
          this.expressNote = this.querySelector('[data-express-note]');
          this.sticky = this.dataset.sticky ? document.getElementById(this.dataset.sticky) : null;

          this.addEventListener('change', this.onChange.bind(this));
          const fromUrl = new URLSearchParams(window.location.search).get('variant');
          if (fromUrl) this.pick(fromUrl);
          this.form.addEventListener('submit', this.onSubmit.bind(this));
          this.update();
          this.setupSticky();
        }

        // Select a face in every picker (used by ?variant= links and "pick your face" tiles).
        pick(variantId) {
          let found = false;
          this.querySelectorAll('[data-slot] input').forEach((input) => {
            if (input.value === String(variantId) && !input.disabled) {
              input.checked = true;
              found = true;
            }
          });
          if (!found) return false;
          this.showFace(this.querySelector(`[data-slot="1"] input[value="${variantId}"]`));
          this.update();
          return true;
        }

        // Tell the gallery which face to show.
        showFace(input) {
          if (!input) return;
          document.dispatchEvent(
            new CustomEvent('mk:show-face', { detail: { face: input.dataset.face, mediaId: input.dataset.mediaId } })
          );
        }

        get offer() {
          return this.querySelector('input[data-offer]:checked');
        }

        get quantity() {
          return this.offer ? Number(this.offer.value) : 1;
        }

        // One face slot per mask in the chosen offer.
        picks() {
          return [...this.querySelectorAll('[data-slot]')]
            .filter((slot) => Number(slot.dataset.slot) <= this.quantity)
            .map((slot) => slot.querySelector('input:checked'))
            .filter(Boolean)
            .map((input) => input.value);
        }

        buildItems() {
          const hasPicker = this.querySelector('[data-slot]');
          if (!hasPicker) return [{ id: Number(this.idInput.value), quantity: this.quantity }];

          const counts = {};
          this.picks().forEach((id) => (counts[id] = (counts[id] || 0) + 1));
          return Object.entries(counts).map(([id, quantity]) => ({ id: Number(id), quantity }));
        }

        onChange(event) {
          const input = event.target;
          if (input.closest('[data-slot]')) this.showFace(input);
          else if (input.matches('[data-offer]')) this.showFace(this.querySelector('[data-slot="1"] input:checked'));
          this.update();
        }

        update() {
          const qty = this.quantity;
          this.querySelectorAll('[data-slot]').forEach((slot) => {
            slot.hidden = Number(slot.dataset.slot) > qty;
            const checked = slot.querySelector('input:checked');
            const name = slot.querySelector('[data-selected-name]');
            if (name) name.textContent = checked ? checked.dataset.name : '';
            const label = slot.querySelector('[data-slot-label]');
            if (label) label.textContent = qty > 1 ? label.dataset.bundle : label.dataset.single;
          });

          const items = this.buildItems();
          const pickedAll = !this.querySelector('[data-slot]') || this.picks().length === qty;
          if (items.length) this.idInput.value = items[0].id;

          // Express checkout can only buy one variant, so hide it when different faces are picked.
          const oneVariant = items.length === 1;
          this.qtyInput.value = oneVariant ? qty : 1;
          if (this.express) this.express.hidden = !oneVariant;
          if (this.expressNote) this.expressNote.hidden = oneVariant;

          const price = this.offer ? this.offer.dataset.price : '';
          const offer = this.offer ? this.offer.dataset.title : '';
          const was = this.offer ? this.offer.dataset.was : '';
          [this, this.sticky].forEach((root) => {
            if (!root) return;
            root.querySelectorAll('[data-current-price]').forEach((el) => (el.textContent = price));
            root.querySelectorAll('[data-current-offer]').forEach((el) => (el.textContent = offer));
            root.querySelectorAll('[data-current-was]').forEach((el) => {
              el.textContent = was;
              el.hidden = !was;
            });
          });

          if (this.button && !this.button.hasAttribute('data-sold-out')) this.button.disabled = !pickedAll;

          const firstFace = this.querySelector('[data-slot="1"] input:checked');
          const stickyThumb = this.sticky && this.sticky.querySelector('.mk-sticky__thumb');
          if (stickyThumb && firstFace && firstFace.dataset.thumb) {
            stickyThumb.removeAttribute('srcset');
            stickyThumb.src = firstFace.dataset.thumb;
          }
        }

        showError(message) {
          if (!this.errorEl) return;
          this.errorEl.textContent = message || '';
          this.errorEl.hidden = !message;
        }

        async onSubmit(event) {
          event.preventDefault();
          if (this.button.getAttribute('aria-busy') === 'true') return;

          const cart = document.querySelector('cart-drawer');
          const body = { items: this.buildItems() };
          if (cart) {
            body.sections = cart.getSectionsToRender().map((section) => section.id);
            body.sections_url = window.location.pathname;
            cart.setActiveElement(this.button);
          }

          this.showError('');
          this.button.setAttribute('aria-busy', 'true');

          try {
            const response = await fetch(`${window.routes.cart_add_url}.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify(body),
            });
            const data = await response.json();

            if (data.status) {
              this.showError(data.description || data.message || 'Could not add that to your cart.');
              return;
            }
            if (!cart) {
              window.location = window.routes.cart_url;
              return;
            }
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'offer-picker', cartData: data });
            }
            cart.classList.remove('is-empty');
            cart.renderContents(data);
          } catch (error) {
            console.error(error);
            this.showError('Something went wrong. Check your connection and try again.');
          } finally {
            this.button.removeAttribute('aria-busy');
          }
        }

        setupSticky() {
          if (!this.sticky || !this.button || !('IntersectionObserver' in window)) return;
          const stickyButton = this.sticky.querySelector('[data-sticky-add]');
          stickyButton.addEventListener('click', () => this.form.requestSubmit(this.button));

          new IntersectionObserver(([entry]) => {
            const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.sticky.classList.toggle('is-visible', scrolledPast);
            this.sticky.setAttribute('aria-hidden', String(!scrolledPast));
            stickyButton.tabIndex = scrolledPast ? 0 : -1;
            const change = this.sticky.querySelector('.mk-sticky__change');
            if (change) change.tabIndex = scrolledPast ? 0 : -1;
          }).observe(this.button);
        }
      }
    );
  }

  if (!customElements.get('mk-gallery')) {
    customElements.define(
      'mk-gallery',
      class MkGallery extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('.mk-gallery__track');
          if (!this.track) return;
          this.dots = [...this.querySelectorAll('.mk-gallery__dot')];
          this.slides = [...this.track.children];
          this.prev = this.querySelector('[data-mk-prev]');
          this.next = this.querySelector('[data-mk-next]');

          this.dots.forEach((dot) =>
            dot.addEventListener('click', () => this.goTo(document.getElementById(dot.dataset.target)))
          );
          if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
          if (this.next) this.next.addEventListener('click', () => this.step(1));
          this.track.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowLeft') this.step(-1);
            if (event.key === 'ArrowRight') this.step(1);
          });

          let frame;
          this.track.addEventListener(
            'scroll',
            () => {
              cancelAnimationFrame(frame);
              frame = requestAnimationFrame(() => this.sync());
            },
            { passive: true }
          );
          this.track.addEventListener('scrollend', () => this.sync());

          document.addEventListener('mk:show-face', (event) => this.showFace(event.detail.face, event.detail.mediaId));
          document.addEventListener('mk:show-media', (event) => {
            const slide = this.slides.find((s) => s.dataset.mediaId === String(event.detail.mediaId));
            if (slide) this.goTo(slide);
          });

          this.querySelectorAll('[data-mk-play]').forEach((button) =>
            button.addEventListener('click', () => this.play(button.closest('.mk-gallery__slide')))
          );

          if ('IntersectionObserver' in window) {
            const observer = new IntersectionObserver(
              (entries) => entries.forEach((entry) => this.onSlideVisible(entry)),
              { root: this.track, threshold: 0.6 }
            );
            this.slides.forEach((slide) => observer.observe(slide));
          }

          // Start on the face already picked in the buy box (from ?variant= or the default).
          const scope = this.closest('#buy') || document;
          const picked = scope.querySelector('[data-slot="1"] input:checked');
          if (picked) this.showFace(picked.dataset.face, picked.dataset.mediaId, true);
          this.sync();
        }

        get visible() {
          return this.slides.filter((slide) => !slide.hidden);
        }

        // Every photo stays in the carousel; picking a face jumps to that face's photo.
        showFace(face, mediaId, instant) {
          const target =
            this.slides.find((slide) => slide.dataset.mediaId === String(mediaId)) ||
            (face && this.slides.find((slide) => slide.dataset.face === face));
          if (target) this.goTo(target, instant);
          this.sync();
        }

        current() {
          const visible = this.visible;
          const left = this.track.scrollLeft;
          let best = 0;
          visible.forEach((slide, i) => {
            if (Math.abs(slide.offsetLeft - this.track.offsetLeft - left) < Math.abs(visible[best].offsetLeft - this.track.offsetLeft - left)) best = i;
          });
          return best;
        }

        step(direction) {
          const visible = this.visible;
          const index = Math.min(Math.max(this.current() + direction, 0), visible.length - 1);
          this.goTo(visible[index]);
        }

        goTo(slide, instant) {
          if (!slide) return;
          this.track.scrollTo({
            left: slide.offsetLeft - this.track.offsetLeft,
            behavior: instant || mkReducedMotion ? 'auto' : 'smooth',
          });
        }

        sync() {
          const visible = this.visible;
          const index = this.current();
          const slide = visible[index];
          if (this.prev) {
            this.prev.disabled = index === 0;
            this.prev.hidden = visible.length < 2;
          }
          if (this.next) {
            this.next.disabled = index >= visible.length - 1;
            this.next.hidden = visible.length < 2;
          }
          if (slide) {
            this.dots.forEach((dot) => dot.setAttribute('aria-current', String(dot.dataset.target === slide.id)));
          }
        }

        onSlideVisible(entry) {
          const slide = entry.target;
          const video = slide.querySelector('video');

          if (entry.isIntersecting) {
            // Muted autoplay only once the page has settled, and never for reduced motion or data saver.
            const saveData = navigator.connection && navigator.connection.saveData;
            if (video && !video.dataset.userStarted && !mkReducedMotion && !saveData) {
              const start = () => {
                video.muted = true;
                video.play().catch(() => {});
              };
              document.readyState === 'complete' ? start() : window.addEventListener('load', start, { once: true });
            }
          } else if (video && !video.paused) {
            video.pause();
          }
        }

        play(slide) {
          const button = slide.querySelector('[data-mk-play]');
          const video = slide.querySelector('video');
          const embed = slide.querySelector('template[data-mk-embed]');

          if (video) {
            video.dataset.userStarted = 'true';
            video.muted = false;
            video.controls = true;
            video.play().catch(() => {});
          } else if (embed) {
            slide.appendChild(embed.content.firstElementChild.cloneNode(true));
          }
          if (button) button.remove();
        }
      }
    );
  }

  if (!customElements.get('delivery-estimate')) {
    customElements.define(
      'delivery-estimate',
      class DeliveryEstimate extends HTMLElement {
        connectedCallback() {
          const min = Number(this.dataset.min);
          const max = Number(this.dataset.max);
          const output = this.querySelector('[data-range]');
          if (!output || Number.isNaN(min) || Number.isNaN(max)) return;

          const format = new Intl.DateTimeFormat(document.documentElement.lang || 'en-US', {
            month: 'short',
            day: 'numeric',
          });
          const from = format.format(this.addBusinessDays(new Date(), min));
          const to = format.format(this.addBusinessDays(new Date(), Math.max(min, max)));
          output.textContent = from === to ? from : `${from} – ${to}`;
          this.hidden = false;
        }

        addBusinessDays(date, days) {
          const result = new Date(date);
          let added = 0;
          while (added < days) {
            result.setDate(result.getDate() + 1);
            const day = result.getDay();
            if (day !== 0 && day !== 6) added += 1;
          }
          return result;
        }
      }
    );
  }

  if (!customElements.get('mk-ugc-tile')) {
    customElements.define(
      'mk-ugc-tile',
      class MkUgcTile extends HTMLElement {
        connectedCallback() {
          this.video = this.querySelector('video');
          this.button = this.querySelector('button');
          if (!this.video || !this.button) return;
          this.video.muted = true;

          // Silent preview while the clip is on screen, like a feed.
          if (!mkReducedMotion && 'IntersectionObserver' in window) {
            new IntersectionObserver(
              (entries) => {
                entries.forEach((entry) => {
                  if (!this.video.muted) return;
                  if (entry.isIntersecting) this.video.play().catch(() => {});
                  else this.video.pause();
                });
              },
              { threshold: 0.6 }
            ).observe(this);
          }

          // Tap: play from the start with sound. Tap again: pause.
          this.button.addEventListener('click', () => {
            const withSound = !this.video.paused && !this.video.muted;
            if (withSound) {
              this.video.pause();
              return;
            }
            document.querySelectorAll('mk-ugc-tile video').forEach((v) => {
              if (v !== this.video && !v.muted) {
                v.muted = true;
                v.pause();
              }
            });
            if (this.video.muted) this.video.currentTime = 0;
            this.video.muted = false;
            this.video.play().catch(() => {});
          });
          const sync = () => this.button.setAttribute('aria-pressed', String(!this.video.paused && !this.video.muted));
          this.video.addEventListener('play', sync);
          this.video.addEventListener('pause', sync);
          this.video.addEventListener('volumechange', sync);
        }
      }
    );
  }

  // Face tiles and spotlight buttons: pick the face in the buy box on this page instead of reloading.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[data-mk-pick]');
    if (!link) return;
    const picker = document.querySelector('#buy offer-picker');
    if (!picker || !picker.pick(link.dataset.mkPick)) return;
    event.preventDefault();
    document.getElementById('buy').scrollIntoView({ behavior: mkReducedMotion ? 'auto' : 'smooth', block: 'start' });
  });

  // Star line in the buy box, filled from the real reviews in the reviews section on this page.
  document.querySelectorAll('[data-mk-rating]').forEach((line) => {
    const summary = document.querySelector('[data-mk-review-summary]');
    const count = summary ? Number(summary.dataset.count) : 0;
    if (!count) return;
    const average = Number(summary.dataset.average);
    const stars = line.querySelector('.mk-stars');
    if (stars) {
      stars.style.setProperty('--mk-stars', `${average * 20}%`);
      stars.setAttribute('aria-label', `${average} out of 5 stars`);
    }
    line.querySelector('[data-mk-rating-text]').textContent = `${average} · ${count} ${count === 1 ? 'review' : 'reviews'}`;
    line.hidden = false;
  });
})();
